import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import formatDate from "shared/@utils/formatDate";
import PermissionBubble from "../PermissionBubble";
import { TagBubble } from "shared/TagBubble";
import { ListRowActions } from "shared/ListRowActions";
import styles from "./AudioListItem.module.css";

function AudioListItem({
  id,
  thumbnail,
  title,
  date,
  speaker,
  episode,
  sku,
  type,
  status,
  isDraft,
  pinPermissions,
  membershipPermissions,
  showMembershipPermissions,
  isHistoryView,
  tags,
  selectable,
  selected,
  layoutVariant,
  onSelect,
  onDuplicate,
  onArchive,
  onEdit,
  disabled,
  isEditLoading,
  isDuplicateLoading,
  isArchiveLoading,
}) {
  const tagRowRef = useRef(null);
  const tagMeasureRef = useRef(null);
  const [showTagOverflowDots, setShowTagOverflowDots] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState(tags.length);
  const isArchived = String(status || "").toLowerCase() === "archived";

  const activePinPermissions = [
    { key: "ibo", label: "IBO", tone: "neutral" },
    { key: "platinum", label: "PLA", tone: "light" },
    { key: "emerald", label: "EME", tone: "emerald" },
    { key: "diamond", label: "DIA", tone: "teal" },
  ].filter((item) => Boolean(pinPermissions?.[item.key]));

  const activeMembershipPermissions = [
    { key: "complimentary", label: "C", tone: "gray" },
    { key: "premier", label: "P", tone: "teal" },
  ].filter((item) => Boolean(membershipPermissions?.[item.key]));

  const showActivePinPermissions = activePinPermissions.length > 0;
  const showActiveMembershipPermissions =
    showMembershipPermissions && activeMembershipPermissions.length > 0;
  const showPermissionDivider =
    showActivePinPermissions && showActiveMembershipPermissions;

  const handleSelect = () => {
    if (onSelect) {
      onSelect(id);
    }
  };

  useEffect(() => {
    const updateOverflowState = () => {
      if (!tagRowRef.current || !tagMeasureRef.current || !tags.length) {
        setShowTagOverflowDots(false);
        setVisibleTagCount(tags.length);
        return;
      }

      const rowWidth = tagRowRef.current.clientWidth;
      const tagWidths = Array.from(tagMeasureRef.current.children).map(
        (node) => node.offsetWidth,
      );

      const gap = 6;
      const overflowDotsWidth = 25;

      let used = 0;
      let visible = 0;

      for (let i = 0; i < tagWidths.length; i += 1) {
        const nextWidth = (visible > 0 ? gap : 0) + tagWidths[i];
        if (used + nextWidth <= rowWidth) {
          used += nextWidth;
          visible += 1;
        } else {
          break;
        }
      }

      const hasOverflow = visible < tags.length;

      if (hasOverflow) {
        while (visible > 1 && used + overflowDotsWidth > rowWidth) {
          const removedTagWidth = tagWidths[visible - 1];
          used -= removedTagWidth;
          if (visible > 1) {
            used -= gap;
          }
          visible -= 1;
        }
      }

      setVisibleTagCount(Math.max(visible, hasOverflow ? 1 : 0));
      setShowTagOverflowDots(hasOverflow);
    };

    updateOverflowState();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateOverflowState);
      if (tagRowRef.current) {
        observer.observe(tagRowRef.current);
      }
    } else {
      window.addEventListener("resize", updateOverflowState);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", updateOverflowState);
      }
    };
  }, [tags]);

  return (
    <article className={styles.container} aria-label={`Audio item ${title}`}>
      <div className={styles.topRow}>
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={`${title} thumbnail`}
            className={styles.thumbnail}
          />
        ) : (
          <div className={styles.thumbnailEmpty} aria-label="No thumbnail">
            No Image
          </div>
        )}

        <div
          className={classNames(styles.content, {
            [styles.contentHistory]: isHistoryView,
            [styles.contentAudios]:
              !isHistoryView && layoutVariant === "audios",
          })}
        >
          <div
            className={classNames(styles.infoCluster, {
              [styles.infoClusterHistory]: isHistoryView,
              [styles.infoClusterAudios]:
                !isHistoryView && layoutVariant === "audios",
            })}
          >
            <div className={styles.metaColumn}>
              <p className={styles.title}>{title}</p>
              <p className={styles.date}>{formatDate(date, "M/DD/YYYY")}</p>
              {speaker && <p className={styles.speaker}>{speaker}</p>}
              {episode && <p className={styles.episode}>{episode}</p>}
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.summaryRow}>
                <p className={styles.valueCell}>{sku}</p>
                <p className={styles.valueCell}>{type}</p>
                <p className={classNames(styles.valueCell, styles.statusCell)}>
                  <span
                    className={classNames(styles.statusText, {
                      [styles.archivedStatusText]: isArchived,
                    })}
                  >
                    {status}
                    {isDraft && (
                      <span className={styles.draftText}> | DRAFT</span>
                    )}
                  </span>
                </p>
              </div>

              <div
                className={classNames(styles.permissionsRow, {
                  [styles.permissionsRowPinOnly]:
                    !showActiveMembershipPermissions,
                })}
              >
                {showActivePinPermissions ? (
                  <div
                    className={styles.permissionGroup}
                    aria-label="Pin permissions"
                  >
                    {activePinPermissions.map((permission) => (
                      <PermissionBubble
                        key={permission.key}
                        label={permission.label}
                        tone={permission.tone}
                        active
                      />
                    ))}
                  </div>
                ) : null}

                {showPermissionDivider ? (
                  <span
                    className={styles.permissionDivider}
                    aria-hidden="true"
                  />
                ) : null}

                {showActiveMembershipPermissions ? (
                  <div
                    className={styles.permissionGroup}
                    aria-label="Membership permissions"
                  >
                    {activeMembershipPermissions.map((permission) => (
                      <PermissionBubble
                        key={permission.key}
                        label={permission.label}
                        tone={permission.tone}
                        active
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div
            className={classNames(styles.rightActionArea, {
              [styles.rightActionAreaSelectable]: selectable,
            })}
          >
            {selectable ? (
              <label className={styles.selectableLabel}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={handleSelect}
                  className={styles.checkbox}
                  aria-label={`Select ${title}`}
                />
              </label>
            ) : (
              <ListRowActions
                onDuplicate={onDuplicate}
                onArchive={onArchive}
                onEdit={onEdit}
                isArchived={isArchived}
                disabled={disabled}
                isEditLoading={isEditLoading}
                isDuplicateLoading={isDuplicateLoading}
                isArchiveLoading={isArchiveLoading}
              />
            )}
          </div>
        </div>
      </div>

      {!!tags.length && (
        <>
          <div
            className={styles.tagMeasureRow}
            ref={tagMeasureRef}
            aria-hidden="true"
          >
            {tags.map((tag, index) => (
              <TagBubble key={`${id}-${tag}-${index}`} label={tag} />
            ))}
          </div>

          <div className={styles.tagRow} ref={tagRowRef}>
            <div className={styles.tagTrack}>
              {tags.slice(0, visibleTagCount).map((tag, index) => (
                <TagBubble key={`${id}-${tag}-${index}`} label={tag} />
              ))}
            </div>

            {showTagOverflowDots && (
              <span className={styles.overflowDots} aria-hidden="true">
                <span className={styles.overflowDot} />
                <span className={styles.overflowDot} />
                <span className={styles.overflowDot} />
              </span>
            )}
          </div>
        </>
      )}
    </article>
  );
}

AudioListItem.propTypes = {
  id: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  speaker: PropTypes.string,
  episode: PropTypes.string,
  sku: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["Audio", "Video", "Music"]).isRequired,
  status: PropTypes.oneOf(["Available", "In Review", "Archived"]).isRequired,
  isDraft: PropTypes.bool,
  pinPermissions: PropTypes.shape({
    ibo: PropTypes.bool,
    platinum: PropTypes.bool,
    emerald: PropTypes.bool,
    diamond: PropTypes.bool,
  }),
  membershipPermissions: PropTypes.shape({
    complimentary: PropTypes.bool,
    premier: PropTypes.bool,
  }),
  showMembershipPermissions: PropTypes.bool,
  isHistoryView: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.string),
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  layoutVariant: PropTypes.oneOf(["default", "audios"]),
  onSelect: PropTypes.func,
  onDuplicate: PropTypes.func,
  onArchive: PropTypes.func,
  onEdit: PropTypes.func,
  disabled: PropTypes.bool,
  isEditLoading: PropTypes.bool,
  isDuplicateLoading: PropTypes.bool,
  isArchiveLoading: PropTypes.bool,
};

AudioListItem.defaultProps = {
  thumbnail: "",
  speaker: "",
  isDraft: false,
  pinPermissions: {},
  membershipPermissions: {},
  showMembershipPermissions: true,
  isHistoryView: false,
  tags: [],
  episode: undefined,
  selectable: false,
  selected: false,
  layoutVariant: "default",
  onSelect: undefined,
  onDuplicate: undefined,
  onArchive: undefined,
  onEdit: undefined,
  disabled: false,
  isEditLoading: false,
  isDuplicateLoading: false,
  isArchiveLoading: false,
};

export default AudioListItem;
